import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { useGetFilterSelections } from '@/catalog-apps/hooks/useGetFilterSelections'
import { CATALOG_BASE_PATH } from '@/const'
import { ENDPOINTS } from '@/util'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { useSubmitPageFilters } from '../../AppPage2/Filter/useSubmitPageFilters'
import { useCatalogComponentRuntime } from '../shared/CatalogComponentRuntimeContext'
import { FileDownload, FormSubmitResponse } from './types'
const url = `${ENDPOINTS.CC_DOTNET}/event`

type SubmitFormOptions = {
  onSuccess?: () => void
}

function blobPartsFromFileDownload(download: FileDownload): BlobPart {
  const encoding = download.contentEncoding ?? 'utf-8'
  if (encoding === 'base64') {
    const sanitized = download.content.replace(/\s/g, '')
    const binary = atob(sanitized)
    return Uint8Array.from(binary, (c) => c.charCodeAt(0))
  }
  return download.content
}

function triggerBrowserDownload(download: FileDownload): boolean {
  try {
    const blob = new Blob([blobPartsFromFileDownload(download)], {
      type: download.mediaType || 'application/json',
    })
    const blobUrl = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = blobUrl
    anchor.download = download.filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(blobUrl)
    return true
  } catch (err) {
    console.error('triggerBrowserDownload failed:', err)
    return false
  }
}

export const useSubmitForm = (
  handlerId: string,
  tileFilterSelections: Record<string, unknown>[],
  actionId?: string,
  resetForm?: (() => void) | SubmitFormOptions
) => {
  const queryClient = useQueryClient()
  const { appKey, projectId } = useAppContext()
  const { filterSelections } = useGetFilterSelections()
  const { pageFilterSelectionsOverride, allowRouteNavigation = true } = useCatalogComponentRuntime()
  const token = JSON.parse(sessionStorage.getItem('token') || '')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Handle the legacy case where resetForm is a function
  const options: SubmitFormOptions = typeof resetForm === 'function' ? { onSuccess: resetForm } : resetForm || {}
  const { submitFilters } = useSubmitPageFilters()
  const effectivePageFilters = pageFilterSelectionsOverride ?? filterSelections

  return useMutation<FormSubmitResponse, Error, Record<string, unknown>>({
    mutationFn: async (formData: Record<string, unknown>) => {
      if (!token) {
        throw new Error('No token found')
      }
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          app_id: appKey,
          content_id: handlerId,
          action_id: actionId || '',
          project_id: projectId,
          filters: [...effectivePageFilters, ...tileFilterSelections],
          // Table-level actions have no row data; omitting payload breaks .NET catalog event parsing.
          payload: formData ?? {},
        }),
      })
      const data = (await response.json()) as FormSubmitResponse
      if (!response.ok) {
        throw new Error(data?.uiSchema?.message || `Request failed (${response.status})`)
      }
      return data
    },
    onSuccess: ({
      uiSchema: { contentToRefresh = [], message, success, showToast, navigation, fileDownload },
    }) => {
      // Temporarily disabled until I get the success property back
      if (typeof success === 'boolean' && success === false) {
        // throw message
        toast.error(message || 'Error')
        return
      }
      if (fileDownload) {
        const downloadOk = triggerBrowserDownload(fileDownload)
        if (!downloadOk) {
          toast.error('File download failed')
          return
        }
      }
      if (showToast !== false) {
        toast.success(message || 'Success')
      }
      if (contentToRefresh && contentToRefresh.length) {
        contentToRefresh.forEach((contentId: string) => {
          queryClient.invalidateQueries({ queryKey: [contentId] })
        })
      }
      if (navigation) {
        if (!allowRouteNavigation) {
          toast.info('Open this component on the page to continue with navigation actions.')
        } else {
          if (navigation.filters) {
            submitFilters(navigation.filters)
          }
          navigate(`${CATALOG_BASE_PATH}${navigation.pageId}?${searchParams.toString()}`)
        }
      }
      // Call the success callback if provided in options
      if (options.onSuccess) {
        options.onSuccess()
      }
    },
    onError: (error) => {
      console.error('FormComponent submitHandler error', error)
      toast.error(error.message || 'Something went wrong. Please try again.')
    },
  })
}
